import { useState } from "react";
import QuestionLayout from "./QuestionLayout";
import AudioPlayButton from "./AudioPlayButton";
import { renderStaffLines, renderClef } from "./staff/staffRender";
import {
  snapYToPitch,
  pitchToY,
  pitchToStaffIndex
} from "./staff/pitchMath";
import { Glyph } from "./staff/glyphs.jsx";

const STAFF_TOP = 40;
const STAFF_HEIGHT = 100;

const LEFT_MARGIN = 100;
const RIGHT_MARGIN = 20;
const MEASURE_WIDTH = 120;

const SVG_HEIGHT = 165;

function LedgerLines({ pitch, clef, symbol }) {
  const NOTEHEAD_METRICS = {
    whole_note: { width: 20, offsetX: -1.5 },
    half_note: { width: 16.5, offsetX: -3 },
    quarter_note: { width: 17.5, offsetX: -3 }
  };

  const { width, offsetX } =
    NOTEHEAD_METRICS[symbol] ?? NOTEHEAD_METRICS.quarter_note;

  const index = pitchToStaffIndex(pitch, clef);
  if (index >= -1 && index <= 9) return null;

  const x1 = offsetX - 4;
  const x2 = offsetX + width + 4;

  const lines = [];

  if (index === -2) lines.push(0);
  if (index === -3) lines.push(-5);
  if (index === -4) lines.push(0, -10);
  if (index === -5) lines.push(-5, -15);

  if (index === 10) lines.push(0);
  if (index === 11) lines.push(5);
  if (index === 12) lines.push(0, 10);
  if (index === 13) lines.push(5, 15);

  return (
    <g>
      {lines.map((y, i) => (
        <line
          key={i}
          x1={x1}
          x2={x2}
          y1={y}
          y2={y}
          stroke="#000"
          strokeWidth={1}
        />
      ))}
    </g>
  );
}

function accidentalFromPitch(pitch) {
  if (!pitch) return null;
  if (pitch.includes("##")) return "##";
  if (pitch.includes("bb")) return "bb";
  if (pitch.includes("#")) return "#";
  if (pitch.includes("b")) return "b";
  return null;
}

function sameStaffPosition(a, b, clef) {
  if (!a || !b) return false;
  return pitchToStaffIndex(a, clef) === pitchToStaffIndex(b, clef);
}

function normalizeMeasureGivenNotes(measureGivenNotes) {
  if (!measureGivenNotes) return [];

  if (Array.isArray(measureGivenNotes)) {
    return measureGivenNotes
      .map((note, index) => {
        if (typeof note === "string") {
          return { slot: index + 1, pitch: note };
        }

        return note;
      })
      .filter((note) => note?.pitch);
  }

  return Object.entries(measureGivenNotes).flatMap(([slot, entry]) => {
    const pitches = entry?.pitches || [];

    return pitches.map((pitch) => ({
      slot: Number(slot),
      pitch,
      symbol: entry.symbol,
      stemUp: entry.stemUp
    }));
  });
}

export default function CompleteTriadByEarWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const [hover, setHover] = useState(null);

  const clef = config.clef || "treble";
  const symbol = config.symbol || "whole_note";
  const measures = config.measures || 6;
  const slotsPerMeasure = config.slotsPerMeasure || 3;
  const inputSlot = config.inputSlot ?? 2;
  const allowLedgerLines = config.allowLedgerLines !== false;

  const svgWidth = LEFT_MARGIN + measures * MEASURE_WIDTH + RIGHT_MARGIN;
  const audioValue = value._audio || {};

  function setAudioState(audioState) {
    if (mode !== "edit") return;

    onChange({
      ...value,
      _audio: audioState
    });
  }

  function measureX(measure) {
    return LEFT_MARGIN + (measure - 1) * MEASURE_WIDTH;
  }

  function getPointerPitch(e) {
    const svg = e.currentTarget.ownerSVGElement;
    const ctm = svg?.getScreenCTM();

    if (!svg || !ctm) return null;

    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;

    const transformed = pt.matrixTransform(ctm.inverse());
    const y = transformed.y;

    return {
      y,
      pitch: snapYToPitch(y, clef, {
        allowLedgerLines
      })
    };
  }

  function slotX(measure, slot) {
    return (
      measureX(measure) +
      (MEASURE_WIDTH * slot) / (slotsPerMeasure + 1)
    );
  }

  function getPlaced(measure) {
    return value.notes?.[measure] || null;
  }

  function updateNote(measure, pitch) {
    if (mode !== "edit") return;

    const placed = getPlaced(measure);

    const nextNotes = {
      ...(value.notes || {})
    };

    if (placed && sameStaffPosition(placed.pitch, pitch, clef)) {
      delete nextNotes[measure];
    } else {
      nextNotes[measure] = {
        symbol,
        pitch
      };
    }

    onChange({
      ...value,
      notes: nextNotes
    });
  }

  function measureFeedback(measure) {
    return feedback?.parts?.[measure] ?? null;
  }

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      legendImage={config.legendImage}
      mode={mode}
      feedback={feedback}
    >
      <div>
        {config.audio && (
          <div style={{ marginBottom: 18 }}>
            <AudioPlayButton
              src={config.audio}
              value={audioValue}
              onChange={setAudioState}
              mode={mode}
              maxListens={config.maxListens ?? 1}
            />
          </div>
        )}

        <svg width={svgWidth} height={SVG_HEIGHT}>
          {renderStaffLines(svgWidth - 10)}
          {renderClef(clef)}

          {Array.from({ length: measures }).map((_, i) => {
            const measure = i + 1;
            const x = measureX(measure);
            const placed = getPlaced(measure);
            const fb = measureFeedback(measure);

            const givenNotes = normalizeMeasureGivenNotes(
              config.givenNotes?.[measure]
            );

            const reviewStroke =
              mode === "review"
                ? fb?.correct
                  ? "#2e7d32"
                  : "#c62828"
                : "transparent";

            return (
              <g key={measure}>
                {/* example number */}
                <text
                  x={x + MEASURE_WIDTH / 2}
                  y={20}
                  textAnchor="middle"
                  fontSize={15}
                  fontWeight={600}
                  fill="#000"
                  pointerEvents="none"
                >
                  {measure}.
                </text>

                {/* internal barline only — not before first measure */}
                {measure > 1 && (
                <rect
                    x={x}
                    y={STAFF_TOP}
                    width={1}
                    height={40}
                    fill="#000"
                />
                )}

                {/* final double barline */}
                {measure === measures && (
                <>
                    <rect
                    x={x + MEASURE_WIDTH + 3.5}
                    y={STAFF_TOP}
                    width={1}
                    height={40}
                    fill="#000"
                    />
                    <rect
                    x={x + MEASURE_WIDTH + 7}
                    y={STAFF_TOP}
                    width={3}
                    height={40}
                    fill="#000"
                    />
                </>
                )}

                {/* hit area */}
                <rect
                  x={x}
                  y={STAFF_TOP - 30}
                  width={MEASURE_WIDTH}
                  height={STAFF_HEIGHT}
                  fill="transparent"
                  stroke={reviewStroke}
                  strokeWidth={mode === "review" ? 2 : 0}
                  style={{
                    touchAction: "none",
                    cursor: mode === "edit" ? "pointer" : "default"
                  }}
                  onPointerMove={(e) => {
                    if (mode !== "edit") return;

                    // Hover preview is useful for a mouse, but not for touch.
                    if (e.pointerType !== "mouse") return;

                    const position = getPointerPitch(e);
                    if (!position) return;

                    setHover({
                      measure,
                      ...position
                    });
                  }}
                  onPointerLeave={(e) => {
                    if (e.pointerType === "mouse") {
                      setHover(null);
                    }
                  }}
                  onPointerDown={(e) => {
                    if (mode !== "edit") return;

                    e.preventDefault();

                    const position = getPointerPitch(e);
                    if (!position) return;

                    // Do not depend on hover state to place the note.
                    setHover(null);
                    updateNote(measure, position.pitch);
                  }}
                  onPointerCancel={() => setHover(null)}
                />

                {/* given notes */}
                {givenNotes.map((note, index) => {
                  const noteSymbol = note.symbol || symbol;

                  return (
                    <g
                      key={`${measure}-${note.pitch}-${index}`}
                      transform={`translate(
                        ${slotX(measure, note.slot)},
                        ${pitchToY(note.pitch, clef)}
                      )`}
                    >
                      <LedgerLines
                        pitch={note.pitch}
                        clef={clef}
                        symbol={noteSymbol}
                      />

                      <Glyph
                        symbol={noteSymbol}
                        stemUp={note.stemUp}
                        accidental={accidentalFromPitch(note.pitch)}
                      />
                    </g>
                  );
                })}

                {/* student note */}
                {placed && (
                  <g
                    transform={`translate(
                      ${slotX(measure, inputSlot)},
                      ${pitchToY(placed.pitch, clef)}
                    )`}
                  >
                    <LedgerLines
                      pitch={placed.pitch}
                      clef={clef}
                      symbol={placed.symbol || symbol}
                    />

                    <Glyph
                      symbol={placed.symbol || symbol}
                      stemUp={placed.stemUp}
                      accidental={accidentalFromPitch(placed.pitch)}
                    />
                  </g>
                )}

                {/* hover ghost */}
                {hover &&
                  hover.measure === measure &&
                  mode === "edit" &&
                  !placed && (
                    <g
                      transform={`translate(
                        ${slotX(measure, inputSlot)},
                        ${pitchToY(hover.pitch, clef)}
                      )`}
                      opacity={0.5}
                      pointerEvents="none"
                    >
                      <LedgerLines
                        pitch={hover.pitch}
                        clef={clef}
                        symbol={symbol}
                      />

                      <Glyph
                        symbol={symbol}
                        accidental={accidentalFromPitch(hover.pitch)}
                        fill="#999"
                      />
                    </g>
                  )}
              </g>
            );
          })}
        </svg>
      </div>
    </QuestionLayout>
  );
}